import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Mail, MapPin, Send, Sparkles } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { mergeSiteContent } from "../utils/helpers";
import { createContactMessage } from "../lib/firestoreActions";
import { CONTACT_EMAIL } from "../lib/platformData";
import { useToast } from "../context/ToastContext";

const contactIcons = [Calendar, Sparkles, Send];

export default function ContactPage() {
  const [site, setSite] = useState(() => mergeSiteContent());
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    website: "",
  });
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [fallbackMailto, setFallbackMailto] = useState("");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      (err) => console.error("Erreur Firestore Contact:", err)
    );
    return () => off();
  }, []);

  const page = site.pages.contact;
  const cards = useMemo(
    () => (page.cards || []).filter((card) => !card.hidden),
    [page.cards]
  );

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus("");
    setError("");
    setFallbackMailto("");
    setBusy(true);
    try {
      const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
        form.subject || "Contact Unlimited Session"
      )}&body=${encodeURIComponent(`${form.name} <${form.email}>\n\n${form.message}`)}`;
      const result = await createContactMessage(form);
      setForm({ name: "", email: "", subject: "", message: "", website: "" });
      setFallbackMailto(mailto);
      setStatus(
        result.emailSent
          ? "Message envoye par email et sauvegarde dans l'admin."
          : "Message enregistré. L'email est en attente d'envoi ; tu peux aussi nous écrire directement ci-dessous."
      );
      toast.success(result.emailSent ? "Message envoyé avec succès" : "Message enregistré", result.emailSent ? "" : "Envoi de l'email en attente.");
    } catch (err) {
      setError(err.message || "Impossible d'envoyer le message.");
      toast.error("Message non envoyé", err.message || "Vérifie les champs.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white">
      {site.visibility.contactHero && (
      <section className="relative overflow-hidden px-6 pb-20 pt-24 md:px-20 md:pt-32">
        <img
          src={page.heroImage || "/images/studio2.jpg"}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/80 to-black" />
        <div className="relative mx-auto max-w-6xl">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            {page.eyebrow}
          </p>
          <h1 className="max-w-4xl text-5xl font-black uppercase leading-none md:text-7xl">
            {page.title}
          </h1>
          <p className="readable-copy mt-6 max-w-2xl text-lg text-gray-300">
            {page.description}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-sm text-gray-300">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2">
              <Mail size={16} /> {page.email}
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2">
              <MapPin size={16} /> {page.location}
            </span>
          </div>
        </div>
      </section>
      )}

      <section className="px-6 py-16 md:px-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
              Message direct
            </p>
            <h2 className="text-4xl font-black uppercase md:text-5xl">
              Ecris-nous proprement.
            </h2>
            <p className="readable-copy mt-4 text-gray-400">
              Raconte-nous ton projet. Nous te répondrons à {form.email || "ton adresse email"}.
            </p>
          </div>

          <form id="contact-form" onSubmit={submit} className="rounded-lg border border-white/10 bg-zinc-950 p-5 md:p-6">
            {status && (
              <div className="mb-4 rounded-lg border border-white/10 bg-black p-3 text-sm text-gray-200">
                <p>{status}</p>
                {fallbackMailto && (
                  <a
                    href={fallbackMailto}
                    className="mt-3 inline-flex rounded-full bg-white px-4 py-2 text-xs font-bold uppercase text-black"
                  >
                    Ouvrir l'email
                  </a>
                )}
              </div>
            )}
            {error && <p className="mb-4 rounded border border-red-500/30 bg-red-500/10 p-3 text-red-100">{error}</p>}

            <input
              className="hidden"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => updateForm("website", e.target.value)}
            />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">Nom</span>
                <input
                  value={form.name}
                  onChange={(e) => updateForm("name", e.target.value)}
                  required
                  className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateForm("email", e.target.value)}
                  required
                  className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
                />
              </label>
            </div>
            <label className="mt-4 block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">Sujet</span>
              <input
                value={form.subject}
                onChange={(e) => updateForm("subject", e.target.value)}
                className="w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
              />
            </label>
            <label className="mt-4 block">
              <span className="mb-2 block text-xs font-bold uppercase tracking-wide text-gray-400">Message</span>
              <textarea
                value={form.message}
                onChange={(e) => updateForm("message", e.target.value)}
                required
                className="min-h-[150px] w-full rounded border border-white/10 bg-black px-4 py-3 outline-none focus:border-red-500"
              />
            </label>
            <button
              disabled={busy}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-bold uppercase text-black transition hover:bg-red-600 hover:text-white disabled:opacity-60"
            >
              <Send size={16} /> {busy ? "Envoi..." : "Envoyer"}
            </button>
          </form>
        </div>
      </section>

      {site.visibility.contactCards && cards.length > 0 && (
      <section className="px-6 py-16 md:px-20">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-5 md:grid-cols-3">
          {cards.map(({ title, text, href, label }, index) => {
            const Icon = contactIcons[index % contactIcons.length];
            return (
            <a
              key={title}
              href={href?.startsWith("mailto:") ? "#contact-form" : href || "#contact-form"}
              className="group rounded-lg border border-white/10 bg-zinc-950 p-6 transition hover:-translate-y-1 hover:border-red-500/70 hover:bg-zinc-900"
            >
              <div className="mb-8 flex h-11 w-11 items-center justify-center rounded-full bg-white text-black">
                <Icon size={20} />
              </div>
              <h2 className="text-2xl font-bold">{title}</h2>
              <p className="readable-copy mt-3 min-h-[72px] text-gray-400">{text}</p>
              <span className="mt-8 inline-flex items-center text-sm font-bold uppercase tracking-wide text-red-500">
                {label}
              </span>
            </a>
            );
          })}
        </div>
      </section>
      )}
    </main>
  );
}
